import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { supabase } from '../supabaseClient';
import type { ShowToast, ShowConfirm, FetchData } from '../types/appShared';

// ══════════════════════════════════════════════
// useMaterialsController — نفس فكرة useTasksController: كل state والـ
// handlers الخاصة بصفحة Materials (الفورم، مودال الإضافة/التعديل، واستيراد
// Excel) كانت متفرّقة جوه App.jsx. materialsData/setMaterialsData اتسابوا في
// App.jsx (بيتحمّلوا من fetchData المركزية، ومحتاجين يكونوا متاحين لصفحات
// تانية زي Overview/EVM)، فبيتبعتوا هنا كـ parameters لو احتاجهم أي handler.
// ══════════════════════════════════════════════
export interface Material {
    id: string | number;
    system: string;
    item_name: string;
    quantity: number | string;
    unit: string;
    delivery_status: string;
    supplier: string;
    notes: string;
}

interface MaterialForm {
    system: string;
    item_name: string;
    quantity: string | number;
    unit: string;
    delivery_status: string;
    supplier: string;
    notes: string;
}

interface ImportRow extends Record<string, any> {
    _row: number;
    _valid: boolean;
}

interface UseMaterialsControllerParams {
    currentProjectId: string | null;
    fetchData: FetchData;
    showToast: ShowToast;
    showConfirm: ShowConfirm;
}

export function useMaterialsController({ currentProjectId, fetchData, showToast, showConfirm }: UseMaterialsControllerParams) {
    // ── فلتر/بحث ──
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSystem, setSelectedSystem] = useState("All Systems");
    const [selectedStatus, setSelectedStatus] = useState('All');

    // ── مودال الإضافة/التعديل ──
    const [savingMaterial, setSavingMaterial] = useState(false);
    const [showMaterialModal, setShowMaterialModal] = useState(false);
    const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
    const [materialForm, setMaterialForm] = useState<MaterialForm>({
        system: '',
        item_name: '',
        quantity: '',
        unit: '',
        delivery_status: 'Pending',
        supplier: '',
        notes: ''
    });

    // ── استيراد Excel ──
    const [showImportModal, setShowImportModal] = useState(false);
    const [importData, setImportData] = useState<ImportRow[]>([]);
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState('');
    const [importSuccess, setImportSuccess] = useState(false);

    const handleImportExcel = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImportError('');
        setImportData([]);
        setImportSuccess(false);

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const XLSX = (window as any).XLSX;
                if (!XLSX) {
                    setImportError('XLSX library not loaded. Add SheetJS to your project.');
                    return;
                }

                const result = event.target?.result;
                if (!result || typeof result === 'string') {
                    setImportError('Failed to read Excel file. Make sure it\'s a valid .xlsx file.');
                    return;
                }
                const data = new Uint8Array(result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows: Record<string, any>[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

                if (rows.length === 0) {
                    setImportError('No data found in the Excel file.');
                    return;
                }

                // Map columns - flexible mapping
                const mapped: ImportRow[] = rows.map((row, index) => ({
                    _row: index + 2,
                    system: row['System'] || row['system'] || row['SYSTEM'] || '',
                    item_name: row['Item Name'] || row['item_name'] || row['Item'] || row['Material'] || '',
                    quantity: parseInt(row['Quantity'] || row['quantity'] || row['QTY'] || 0) || 0,
                    unit: row['Unit'] || row['unit'] || row['UNIT'] || '',
                    delivery_status: row['Delivery Status'] || row['Status'] || row['delivery_status'] || 'Pending',
                    supplier: row['Supplier'] || row['supplier'] || row['SUPPLIER'] || '',
                    notes: row['Notes'] || row['notes'] || row['NOTES'] || '',
                    project_id: currentProjectId,
                    _valid: !!(row['System'] || row['system']) && !!(row['Item Name'] || row['item_name'] || row['Item'] || row['Material']),
                }));

                setImportData(mapped);
                setShowImportModal(true);
            } catch (err: any) {
                setImportError('Failed to read Excel file. Make sure it\'s a valid .xlsx file.');
            }
        };
        reader.readAsArrayBuffer(file);
        e.target.value = ''; // reset input
    };

    const handleSubmitImport = async () => {
        const validRows = importData.filter(r => r._valid);
        if (validRows.length === 0) {
            setImportError('No valid rows to import.');
            return;
        }

        setImportLoading(true);
        setImportError('');

        try {
            const toInsert = validRows.map(({ _row, _valid, ...rest }) => rest);

            const { error } = await supabase
                .from('materials')
                .insert(toInsert);

            if (error) throw error;

            setImportSuccess(true);
            showToast(`✅ Imported ${validRows.length} materials successfully!`, 'success');

            setTimeout(() => {
                setShowImportModal(false);
                setImportData([]);
                setImportSuccess(false);
                fetchData(currentProjectId);
            }, 1500);

        } catch (err: any) {
            console.error('Materials import error:', err);
            setImportError('Failed to import. Please check the file and try again.');
        } finally {
            setImportLoading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const XLSX = (window as any).XLSX;
        if (!XLSX) return;

        const template = [
            {
                'System': 'CCTV',
                'Item Name': 'IP Camera 4MP',
                'Quantity': 10,
                'Unit': 'pcs',
                'Delivery Status': 'Pending',
                'Supplier': 'Supplier Name',
                'Notes': 'Optional notes'
            },
            {
                'System': 'Fire Alarm',
                'Item Name': 'Smoke Detector',
                'Quantity': 50,
                'Unit': 'pcs',
                'Delivery Status': 'Ordered',
                'Supplier': 'Another Supplier',
                'Notes': ''
            }
        ];

        const ws = XLSX.utils.json_to_sheet(template);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Materials');

        // Column widths
        ws['!cols'] = [
            { wch: 20 }, { wch: 30 }, { wch: 12 },
            { wch: 12 }, { wch: 20 }, { wch: 25 }, { wch: 30 }
        ];

        XLSX.writeFile(wb, 'Materials_Import_Template.xlsx');
    };

    // Add/Edit Material
    const handleSaveMaterial = async () => {
        try {
            // Validation
            if (!materialForm.system || !materialForm.item_name || !materialForm.quantity || !materialForm.unit) {
                showToast('Please fill all required fields!');
                return;
            }

            setSavingMaterial(true);

            const materialData = {
                project_id: currentProjectId,
                system: materialForm.system.trim(),
                item_name: materialForm.item_name.trim(),
                quantity: parseInt(String(materialForm.quantity)) || 0,
                unit: materialForm.unit.trim(),
                delivery_status: materialForm.delivery_status,
                supplier: materialForm.supplier.trim(),
                notes: materialForm.notes.trim()
            };

            if (editingMaterial) {
                const { error } = await supabase
                    .from('materials')
                    .update(materialData)
                    .eq('id', editingMaterial.id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('materials')
                    .insert([materialData]);
                if (error) throw error;
            }

            // Success
            setShowMaterialModal(false);
            setEditingMaterial(null);
            setMaterialForm({
                system: '',
                item_name: '',
                quantity: '',
                unit: '',
                delivery_status: 'Pending',
                supplier: '',
                notes: ''
            });

            // ✅ كانت هنا نسختين مكررة من منطق toast بالظبط (setToast + setTimeout
            // يدوي) بدل استخدام showToast الموجودة فعلاً — استبدلتهم بـ showToast
            // عشان نشيل التكرار؛ نفس السلوك بالضبط (نفس الرسائل، نفس الـ 3 ثواني).
            showToast(editingMaterial ? '✅ Material updated!' : '✅ Material added!', 'success');

            // ✅ كانت ناقصة هنا تحديدًا — handleDeleteMaterial وhandleSubmitImport
            // (الاستيراد) بينداو fetchData بعد العملية، بس handleSaveMaterial (إضافة/تعديل
            // من المودال) لأ. يعني البند الجديد كان بيدخل الداتابيز فعليًا، بس
            // مكانش بيظهر في اللستة (materialsData) إلا لما يحصل refetch تاني من
            // مكان تاني (تبديل تاب/مشروع، أو الـ auto-refresh كل 30 ثانية، أو
            // refresh يدوي للصفحة). إضافة fetchData هنا تخلي الإضافة/التعديل
            // يظهروا في اللستة فورًا زي الحذف والاستيراد بالظبط.
            fetchData(currentProjectId);

        } catch (error: any) {
            console.error('❌ Error:', error);
            showToast('❌ Failed to save material!', 'error');
        } finally {
            setSavingMaterial(false);
        }
    };

    const handleDeleteMaterial = async (materialId: string | number) => {
        showConfirm('Are you sure you want to delete this material? This action cannot be undone.', async () => {
            try {
                const { error } = await supabase
                    .from('materials')
                    .delete()
                    .eq('id', materialId);

                if (error) throw error;

                showToast('✅ Material deleted successfully!');
                fetchData(currentProjectId);

            } catch (error: any) {
                console.error('❌ Error deleting material:', error);
                showToast('❌ Failed to delete material. Please try again.');
            }
        });
    };

    // Open Edit Modal
    const handleEditMaterial = (material: Material) => {
        setEditingMaterial(material);
        setMaterialForm({
            system: material.system || '',
            item_name: material.item_name || '',
            quantity: material.quantity || '',
            unit: material.unit || '',
            delivery_status: material.delivery_status || 'Pending',
            supplier: material.supplier || '',
            notes: material.notes || ''
        });
        setShowMaterialModal(true);
    };

    return {
        searchTerm, setSearchTerm, selectedSystem, setSelectedSystem, selectedStatus, setSelectedStatus,
        showMaterialModal, setShowMaterialModal, editingMaterial, setEditingMaterial,
        materialForm, setMaterialForm, savingMaterial,
        handleSaveMaterial, handleEditMaterial, handleDeleteMaterial,
        showImportModal, setShowImportModal, importData, setImportData,
        importLoading, importError, setImportError, importSuccess,
        handleImportExcel, handleSubmitImport, handleDownloadTemplate,
    };
}

export default useMaterialsController;
